import React from 'react';
import { Box, CircularProgress, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { wrap } from 'comlink';

import { ReactComponent as DownloadSvg } from 'assets/images/download.svg';
import { useBuilding } from 'store/apis';
import { getReportTypeDetails } from './getReportTypeDetails';
import { useServiceZoneReport } from './useServiceZoneReport';
import { useMasterCleaningScheduleReport } from './useMasterCleaningScheduleReport';
import { useZoneSetupReport } from './useZoneSetupReport';
import { useTargetZoneReport } from './useTargetZoneReport';

export type ReportType = 'master-cleaning-schedule' | 'service-zone' | 'target-zone' | 'zone-setup';

interface ReportsTableRowProps {
  type: ReportType;
  buildingId: number;
  start: string;
  end: string;
}

export const ReportsTableRow = ({ type, buildingId, start, end }: ReportsTableRowProps) => {
  const {
    data: { id: building_id, address: building_address },
    isLoading: loading_building,
  } = useBuilding({ buildingId });

  const useReportData =
    type === 'service-zone'
      ? useServiceZoneReport
      : type === 'master-cleaning-schedule'
      ? useMasterCleaningScheduleReport
      : type === 'zone-setup'
      ? useZoneSetupReport
      : useTargetZoneReport;

  const { data, isLoading: loading_report } = useReportData({ buildingId, start, end });

  const { reportName, fileNamePrefix } = getReportTypeDetails({ type });

  const notify = (payload: { toastId: string; fileName: string }) => {
    const { toastId, fileName } = payload;

    const Msg = () => {
      return (
        <Box>
          <Typography variant="body2">Generating {reportName}...</Typography>
          <Typography variant="caption">{fileName}</Typography>
        </Box>
      );
    };

    toast.success(<Msg />, {
      toastId,
      autoClose: false,
      icon: () => (
        <Box sx={{ width: 20, height: 20 }}>
          <CircularProgress size={18} sx={{ color: 'white' }} />
        </Box>
      ),
    });
  };

  const dismiss = (payload: { toastId: string }) => {
    const { toastId } = payload;

    toast.dismiss(toastId);
  };

  const fileName = building_id
    ? `${fileNamePrefix}_${building_address}_${dayjs(start).format('MMDD')}-${dayjs(end).format('MMDD')}.pdf`
    : '';

  return (
    <TableRow>
      <TableCell>
        {loading_building ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ marginRight: 1 }}>
              {fileNamePrefix}
            </Typography>

            <CircularProgress size={16} sx={{ marginLeft: 0.5 }} />
          </Box>
        ) : (
          fileName
        )}
      </TableCell>

      <TableCell>{dayjs(end).format('MM/DD/YY')}</TableCell>

      <TableCell>
        {loading_report ? (
          <Box
            sx={{
              width: 36,
              height: 36,
              minWidth: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={16} />
          </Box>
        ) : (
          <IconButton
            color="primary"
            sx={{ width: 36, height: 36 }}
            disabled={loading_building}
            onClick={async () => {
              const toastId = `${Date.now()}`;

              notify({ toastId, fileName });

              const worker = new Worker(new URL('web-workers', import.meta.url));
              const pdfWorker = wrap<import('web-workers').ReportPdfWebWorker>(worker);

              const {
                pdfBlob,
                fileName: _fileName,
                sessionId,
              }: any = await pdfWorker.reportPdfGenerator({
                type,
                fileName,
                data,
                sessionId: toastId,
              });

              saveAs(pdfBlob, _fileName);
              dismiss({ toastId: sessionId });
            }}
          >
            <DownloadSvg style={{ width: 16 }} />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
};
